/**
 * Seals days in the admin's browser, for a device that never sealed them
 * itself (closed for good, wiped, uninstalled). The browser already holds
 * that device's decrypted records from paging its summaries; it packs the
 * device's own signed records for one finished day into a single envelope,
 * encrypted to the organization's active key, so later loads read that day
 * as one record. Each record keeps the device's signature, so a sealed day
 * holds nothing an admin could have written. The Hub only ever sees
 * ciphertext, and an agent's own seal of the same day always replaces this.
 */
import { bytesToBase64 } from '~/util/bytes';
import { dedupeIntervals } from '~/util/intervalPayload';
import { encryptOrgSummary } from '~/util/orgKeypair';
const DAY_SECONDS = 86400;
function dayStartUnix(day) {
    return Date.parse(`${day}T00:00:00Z`) / 1000;
}
/**
 * Which of `candidates` (days with no sealed envelope at all) this browser
 * can prove complete, so sealing them loses nothing:
 *
 * - every summary that could hold the day's intervals is loaded (a summary
 *   is never received before the activity in it, so loading back to the
 *   day's start is enough);
 * - nothing of the device's failed to decrypt, since an unreadable record
 *   could belong to any day;
 * - every event carries its own start time (older agents' payloads don't,
 *   so their events can't be placed in a day);
 * - the device has already sent an interval that started after the day.
 *   The agent uploads intervals strictly in the order they happened, so
 *   nothing from the day can still be waiting on the device.
 */
export function provablyCompleteDays(candidates, evidence) {
    if (evidence.failedCount > 0)
        return [];
    const events = evidence.payloads.flatMap(p => p.events);
    if (events.some(e => e.start === undefined))
        return [];
    const latestStart = events.reduce((latest, e) => Math.max(latest, e.start), -Infinity);
    return candidates.filter(day => {
        const start = dayStartUnix(day);
        return evidence.loadedFrom <= start && latestStart >= start + DAY_SECONDS;
    });
}
/** The events that started on `day`, oldest first. */
export function eventsStartingOn(day, payloads) {
    const start = dayStartUnix(day);
    return payloads
        .flatMap(p => p.events)
        .filter(e => e.start !== undefined && e.start >= start && e.start < start + DAY_SECONDS)
        .sort((a, b) => a.start - b.start);
}
/** Whether `record` holds an event that started on `day`. */
function touchesDay(record, day) {
    const start = dayStartUnix(day);
    return record.payload.events.some(e => e.start !== undefined && e.start >= start && e.start < start + DAY_SECONDS);
}
/** The sealed day, before compression: the device's signed records that
 * hold the day's intervals, exactly as the device sent them, each once.
 * It isn't signed as a whole: it can only leave records out, never add or
 * change one, and leaving records out is no more than deleting them from
 * the Hub, which whoever runs it can always do. */
export function sealedDayPayload(day, deviceId, records) {
    return {
        kind: 'sealed_records',
        device_id: deviceId,
        day,
        records: Array.from(new Set(records.map(r => bytesToBase64(r.plaintext)))),
    };
}
async function gzip(bytes) {
    const stream = new Blob([bytes]).stream().pipeThrough(new CompressionStream('gzip'));
    return new Uint8Array(await new Response(stream).arrayBuffer());
}
/**
 * Gzips and encrypts one day from the device's signed records, ready for
 * the Hub. Null when any record holding the day's intervals isn't signed
 * (from an agent older than signing): such a day is left to its summaries
 * rather than sealed without proof of where it came from.
 */
export async function sealDay(day, deviceId, records, publicKeyBase64, keyVersion) {
    const touching = records.filter(r => touchesDay(r, day));
    if (touching.some(r => r.status !== 'signed'))
        return null;
    const json = JSON.stringify(sealedDayPayload(day, deviceId, touching));
    const envelope = await encryptOrgSummary(publicKeyBase64, keyVersion, await gzip(new TextEncoder().encode(json)));
    return {
        day,
        interval_count: eventsStartingOn(day, dedupeIntervals(touching.map(r => r.payload))).length,
        ...envelope,
    };
}
